import { logger } from '@/lib/logger';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { ApiErrorResponse } from '@/types/error-type';
import { useConnection, useWriteContract, useWaitForTransactionReceipt, useSignMessage } from 'wagmi';
import { useState, useCallback } from 'react';
import {
  approvalService,
  type AllowanceResponse,
  type ApprovalInfoResponse,
} from '@/services/approval.service';
import type { GasFundingCheckResponse } from '@asetquhub/validation';
import { parseAbi } from 'viem';
import { getReadableBlockchainError } from '@/lib/utils';

/**
 * Query key factory for approval-related queries
 */
export const approvalKeys = {
  all: ['approval'] as const,
  allowance: () => [...approvalKeys.all, 'allowance'] as const,
  approvalInfo: () => [...approvalKeys.all, 'approval-info'] as const,
  gasFunding: () => [...approvalKeys.all, 'gas-funding'] as const,
} as const;

/**
 * Hook to check user's token allowance for backend wallet
 */
export function useCheckAllowance(
  options?: Omit<UseQueryOptions<AllowanceResponse | null, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: approvalKeys.allowance(),
    queryFn: () => approvalService.checkAllowance(),
    staleTime: 1000 * 10, // Cache for 10 seconds
    // Stop polling once approved — approval is permanent (max uint256 allowance)
    refetchInterval: query => (query.state.data?.isApproved ? false : 1000 * 30),
    // 429 is already handled by the axios interceptor (auto-retry with parsed delay);
    // only retry once for other transient errors.
    retry: (failureCount, error) => {
      if (error instanceof ApiErrorResponse && error.status === 429) return false;
      return failureCount < 1;
    },
    retryDelay: 1000,
    ...options,
  });
}

/**
 * Hook to get approval information
 */
export function useApprovalInfo(
  options?: Omit<UseQueryOptions<ApprovalInfoResponse | null, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: approvalKeys.approvalInfo(),
    queryFn: () => approvalService.getApprovalInfo(),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    ...options,
  });
}

/**
 * Hook to check if user needs gas funding for approval
 */
export function useGasFundingCheck(
  options?: Omit<UseQueryOptions<GasFundingCheckResponse | null, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: approvalKeys.gasFunding(),
    queryFn: () => approvalService.checkGasFunding(),
    staleTime: 1000 * 10, // Cache for 10 seconds
    ...options,
  });
}

/**
 * ERC20 ABI for approve function
 */
const ERC20_ABI = parseAbi([
  'function approve(address spender, uint256 amount) public returns (bool)',
]);

/**
 * Hook to handle token approval flow with auto gas-funding
 * Combines allowance check, gas funding check, approval transaction, and status tracking
 */
export function useTokenApproval() {
  const { address, isConnected } = useConnection();
  const [isApproving, setIsApproving] = useState(false);
  const [isFundingGas, setIsFundingGas] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [fundingMessage, setFundingMessage] = useState<string | null>(null);

  // Fetch allowance, approval info, and gas funding check
  const {
    data: allowanceData,
    refetch: refetchAllowance,
    // isPending stays true throughout the entire initial load including retry delays,
    // whereas isLoading (= isPending && isFetching) briefly flips false during retry
    // waits, causing the loading step to flash off and back on.
    isPending: isCheckingAllowance,
    error: allowanceError,
    isError: hasAllowanceError,
  } = useCheckAllowance();
  const { data: approvalInfo } = useApprovalInfo();
  const { data: gasFundingData, isLoading: isCheckingGasFunding } = useGasFundingCheck();

  // Contract write hook for approval
  const {
    writeContract,
    data: hash,
    error: writeError,
    isPending: isWritePending,
  } = useWriteContract();

  // Sign message hook for gas funding authorization
  const { signMessageAsync } = useSignMessage();

  // Wait for transaction receipt
  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash,
  });

  /**
   * Execute approval transaction with auto gas-funding
   * NEW: Includes signature verification for security
   */
  const approve = useCallback(async () => {
    if (!approvalInfo || !address || !isConnected) {
      setApprovalError('Missing approval info or wallet not connected');
      return false;
    }

    setIsApproving(true);
    setApprovalError(null);
    setFundingMessage(null);

    try {
      // Step 1: Check if user needs gas funding (with ALL security checks)
      setFundingMessage('Checking gas balance and security status...');
      const gasFundingCheck = await approvalService.checkGasFunding();

      // Security Check: Blacklisted
      if (gasFundingCheck?.isBlacklisted) {
        throw new Error(
          `Account is blacklisted: ${gasFundingCheck.blacklistReason || 'Security violation'}`
        );
      }

      // Security Check: Already funded
      if (gasFundingCheck?.alreadyFunded) {
        throw new Error(
          'You have already received one-time gas funding. Please approve the tokens within 10 minutes.'
        );
      }

      // Security Check: Minimum balance
      if (!gasFundingCheck?.hasMinimumBalance) {
        throw new Error(
          'Your wallet holds no AsetQu. Any amount (even 0.000000000000000001 AsetQu) is enough to receive gas funding.'
        );
      }

      if (gasFundingCheck?.needsFunding) {
        // Step 2: Generate nonce for signature
        setFundingMessage('Generating authorization request...');
        const nonceData = await approvalService.generateNonce();

        if (!nonceData) {
          throw new Error('Failed to generate authorization request');
        }

        // Step 3: Sign message for gas funding authorization
        setFundingMessage('Please sign the authorization message...');
        const signature = await signMessageAsync({
          message: nonceData.message,
        });

        // Step 4: Auto-fund gas fee with signature
        setIsFundingGas(true);
        setFundingMessage(
          `Funding your wallet with ${gasFundingCheck.minRequiredFormatted} for gas...`
        );

        const fundResult = await approvalService.fundGas({
          signature,
          nonce: nonceData.nonce,
        });

        if (!fundResult?.success) {
          throw new Error('Failed to fund gas fee. Please try again.');
        }

        setFundingMessage(
          `Gas funded! ${fundResult.amountFormatted} received. You have 10 minutes to approve.`
        );

        // Wait a bit for the transaction to be confirmed
        await new Promise(resolve => setTimeout(resolve, 2000));

        setIsFundingGas(false);
      }

      // Step 5: Proceed with token approval
      setFundingMessage('Requesting token approval...');

      // Max uint256 for unlimited approval
      const maxUint256 = BigInt(
        '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'
      );

      writeContract({
        address: approvalInfo.tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [approvalInfo.backendWallet as `0x${string}`, maxUint256],
      });

      return true;
    } catch (error) {
      logger.error('Approval error:', error);
      setApprovalError(getReadableBlockchainError(error, 'Approval gagal. Silakan coba lagi.'));
      setIsApproving(false);
      setIsFundingGas(false);
      setFundingMessage(null);
      return false;
    }
  }, [approvalInfo, address, isConnected, writeContract, signMessageAsync]);

  /**
   * Reset approval state after confirmation
   */
  const resetApproval = useCallback(() => {
    setIsApproving(false);
    setApprovalError(null);
    refetchAllowance();
  }, [refetchAllowance]);

  // Auto-reset when transaction is confirmed
  if (isConfirmed && isApproving) {
    setTimeout(resetApproval, 1000);
  }

  return {
    // State
    isApproved: allowanceData?.isApproved ?? false,
    allowance: allowanceData?.formattedAllowance ?? '0',
    allowanceData,
    approvalInfo,
    isCheckingAllowance,
    hasAllowanceError,
    allowanceError: allowanceError?.message,

    // Gas funding state
    gasFundingData,
    isCheckingGasFunding,

    // Approval transaction state
    isApproving: isApproving || isWritePending || isConfirming,
    isFundingGas,
    isConfirming,
    isConfirmed,
    approvalError: approvalError || (writeError ? getReadableBlockchainError(writeError, 'Approval gagal. Silakan coba lagi.') : null),
    fundingMessage,
    txHash: hash,

    // Actions
    approve,
    refetchAllowance,
    resetApproval,
  };
}
